// src/admin/RaporYazdirma.jsx
//
// TA / 3 Ego raporunun baskıya hazır, Someren Labs antetli sürümü (2026-08-22).
// Desen DISC'ten (disc-app/src/admin/RaporYazdirma.jsx) alınmıştır; iki üründe
// çıktı aynı görünsün diye kasıtlı olarak birebir.
//
// NEDEN TARAYICI BASKISI: rapor gövdesi tamamen HTML/CSS. Tarayıcının kendi
// "PDF olarak kaydet" çıktısı bu yüzden vektörel ve metni seçilebilir olur.
// jsPDF/html2canvas sayfayı görüntüye çevirir — Türkçe karakterlerde ve font
// keskinliğinde kayıp verir, ~1 MB bağımlılık ekler. Gerek yok.

import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import TaEgoPersonalReport from "../report/TaEgoPersonalReport";

export default function RaporYazdirma({
  katilimciAdi,
  sirket,
  takim,
  tarih,
  sureMetni,
  baskinAd = "",
  vurguRenk = "#0ea5e9",
  dil = "tr",
  raporProps,
  onKapat,
}) {
  const tr = dil !== "en";

  // Baskı sırasında sayfanın geri kalanını gizlemek için gövdeye işaret sınıfı.
  //
  // Belge başlığı da burada değişir: tarayıcının yazdırma üstbilgisi sayfa
  // başlığını yazar; aynı başlık "PDF olarak kaydet"te önerilen dosya adı olur.
  // Böylece dosya kendiliğinden "TA 3 Ego Raporu — Ad Soyad.pdf" gelir.
  useEffect(() => {
    document.body.classList.add("sl-baski-aktif");
    const oncekiTasma = document.body.style.overflow;
    const oncekiBaslik = document.title;
    document.body.style.overflow = "hidden";
    document.title = `${tr ? "TA / 3 Ego Raporu" : "TA / 3 Ego Report"} — ${katilimciAdi || "-"}`;
    return () => {
      document.body.classList.remove("sl-baski-aktif");
      document.body.style.overflow = oncekiTasma;
      document.title = oncekiBaslik;
    };
  }, [tr, katilimciAdi]);

  useEffect(() => {
    function tusaBasildi(e) {
      if (e.key === "Escape") onKapat?.();
    }
    window.addEventListener("keydown", tusaBasildi);
    return () => window.removeEventListener("keydown", tusaBasildi);
  }, [onKapat]);

  // Ego renk/ad haritası panelde duruyor; buraya prop olarak geliyor.
  const uretimTarihi = new Date().toLocaleString(tr ? "tr-TR" : "en-GB");

  // PORTAL ŞART: katman doğrudan <body>'nin çocuğu olmalı.
  // Admin ağacının içinde kalsaydı baskıda geri kalanı gizlemek için
  // `visibility:hidden` gerekirdi — o da yerleşimi KORUDUĞU için gizli tablo
  // yüksekliği kâğıtta yer kaplayıp raporun ardına boş sayfalar ekliyordu.
  // Gövde seviyesinde olunca kardeşleri `display:none` ile akıştan çıkarabiliyoruz.
  return createPortal(
    <div style={st.katman} id="sl-baski-katman">
      <style>{BASKI_CSS}</style>

      {/* ── Ekranda görünen araç çubuğu (baskıda gizli) ── */}
      <div className="sl-ekranda" style={st.arac}>
        <div style={st.aracSol}>
          <strong style={{ fontSize: 15 }}>{katilimciAdi || "-"}</strong>
          <span style={st.aracAlt}>
            {tr ? "baskı önizleme" : "print preview"} · {tr ? "Esc ile kapat" : "press Esc to close"}
          </span>
        </div>
        <div style={st.aracSag}>
          <button style={st.dugmeBirincil} onClick={() => window.print()}>
            {tr ? "PDF olarak kaydet / Yazdır" : "Save as PDF / Print"}
          </button>
          <button style={st.dugmeIkincil} onClick={onKapat}>
            {tr ? "Kapat" : "Close"}
          </button>
        </div>
      </div>

      <p className="sl-ekranda" style={st.ipucu}>
        {tr
          ? "Yazıcı penceresinde Hedef olarak “PDF olarak kaydet” seçin. Daha temiz bir çıktı için “Üstbilgi ve altbilgi” kutusunu kapatın, “Arka plan grafikleri” kutusunu açık bırakın."
          : "In the print dialog choose “Save as PDF” as the destination. For a cleaner result turn off “Headers and footers” and keep “Background graphics” on."}
      </p>

      {/* ── Basılan kök ──
          lang ŞART: index.html'de <html lang="en"> yazıyor ve CSS
          text-transform:uppercase büyütmeyi SAYFA DİLİNE göre yapıyor. İngilizce
          kuralla "Şirket" → "ŞIRKET", "İşlem" → "ISLEM" oluyor; noktalı İ
          kayboluyor. lang="tr" doğru büyütür. */}
      <div id="sl-baski-kok" lang={tr ? "tr" : "en"}>
        <div id="sl-baski-sayfa" style={st.sayfa}>
          <header style={st.baslikBandi} className="sl-blok">
            <div style={st.markaSol}>
              <img src="/someren-labs-logo.svg" alt="Someren Labs" style={st.logo} />
            </div>
            <div style={st.markaSag}>
              <p style={st.kicker}>
                {tr ? "KİŞİSEL DEĞERLENDİRME RAPORU" : "PERSONAL ASSESSMENT REPORT"}
              </p>
              <h1 style={st.anaBaslik}>
                {tr ? "TA · Üç Ego Durumu" : "TA · Three Ego States"}
              </h1>
            </div>
          </header>

          <div style={{ ...st.vurguCizgi, background: vurguRenk }} />

          <section style={st.kunye} className="sl-blok">
            <div style={st.kunyeIsim}>
              <span style={st.kunyeEtiket}>{tr ? "Katılımcı" : "Participant"}</span>
              <strong style={st.kunyeDeger}>{katilimciAdi || "-"}</strong>
            </div>

            {baskinAd && (
              <div style={{ ...st.baskinRozet, background: vurguRenk }}>
                {tr ? "Baskın ego" : "Dominant ego"}: {baskinAd}
              </div>
            )}

            <div style={st.kunyeIzgara}>
              <Kunye etiket={tr ? "Şirket / Kurum" : "Company"} deger={sirket} />
              <Kunye etiket={tr ? "Takım" : "Team"} deger={takim} />
              <Kunye etiket={tr ? "Uygulama tarihi" : "Completion date"} deger={tarih} />
              <Kunye etiket={tr ? "Tamamlama süresi" : "Duration"} deger={sureMetni} />
            </div>
          </section>

          {/* baskiModu: bileşen kendi kapağını ve künyesini atlar — o bilgiler
              yukarıdaki antette zaten var. */}
          <TaEgoPersonalReport {...raporProps} baskiModu />

          <footer style={st.altBilgi} className="sl-blok">
            <div>
              <strong>© 2026 Someren Labs</strong> — {tr ? "Tüm hakları saklıdır." : "All rights reserved."}
            </div>
            <div style={st.altBilgiSag}>
              {tr ? "Rapor üretim tarihi" : "Report generated"}: {uretimTarihi}
            </div>
          </footer>
        </div>
      </div>
    </div>,
    document.body
  );
}

function Kunye({ etiket, deger }) {
  return (
    <div style={st.kunyeHucre}>
      <span style={st.kunyeEtiket}>{etiket}</span>
      <strong style={st.kunyeKucukDeger}>{deger || "-"}</strong>
    </div>
  );
}

// ── Baskı kuralları ─────────────────────────────────────────────────────────
// Katman portal ile <body> altına basıldığı için baskıda gövdenin diğer TÜM
// çocuklarını `display:none` ile akıştan çıkarabiliyoruz. `visibility:hidden`
// kullanılsaydı gizlenen admin ekranı yüksekliğini korur ve raporun ardına boş
// sayfalar eklenirdi.
const BASKI_CSS = `
@page { size: A4; margin: 10mm 12mm 12mm; }

@media print {
  body.sl-baski-aktif > *:not(#sl-baski-katman) { display: none !important; }
  body.sl-baski-aktif { overflow: visible !important; height: auto !important; }

  body.sl-baski-aktif #sl-baski-katman {
    position: static !important;
    background: #fff !important;
    padding: 0 !important;
    margin: 0 !important;
    overflow: visible !important;
    height: auto !important;
  }
  #sl-baski-sayfa {
    width: auto !important;
    max-width: none !important;
    margin: 0 !important;
    padding: 0 !important;
    border: 0 !important;
    border-radius: 0 !important;
    box-shadow: none !important;
  }

  .sl-ekranda { display: none !important; }

  /* GÖLGELER BASKIDA KAPALI.
     Rapor kartlarında 24px bulanıklıkta bir box-shadow var — ekranda zarif
     duruyor ama kâğıtta bulanık gri lekeler bırakıyor. Kartlar 14px aralıklı;
     24px'lik bulanıklık aralıkları tamamen doldurup sayfayı gri bantlı
     gösteriyordu. Kâğıtta derinliği çerçeve verir, gölge değil.
     (NOT: bu blok bir template literal — içine BACKTICK yazılmayacak,
      string'i erkenden kapatıp dosyayı bozuyor.) */
  #sl-baski-kok *, #sl-baski-sayfa { box-shadow: none !important; }

  /* Pencere renkleri, rozetler ve dörtlü grafik baskıda da kalmalı;
     renk kaybolursa TA raporu okunmaz hale gelir. */
  #sl-baski-kok {
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }

  /* IZGARA → NORMAL AKIŞ.
     Chrome, CSS Grid kaplarını sayfalara bölerken ızgara ELEMANLARININ
     break-inside kuralını UYGULAMIYOR: kartlar sayfa ortasından kesiliyordu
     ("Koç Bakışı" başlığı bir sayfada, metninin yarısı ötekinde). Ayrıca ızgara
     satırları eşit yüksekliğe gerildiği için kısa kartın altında boşluk kalıyordu.
     Baskıda blok akışa çeviriyoruz: iki sütunlu görünüm korunur ama kartlar
     normal akışta yer aldığı için bölünme kuralı çalışır.
     (DISC baskı katmanında aynı kural var; oradan alındı.) */
  #sl-baski-kok .sl-akis { display: block !important; }
  #sl-baski-kok .sl-akis > * { margin-bottom: 14px !important; }

  #sl-baski-kok .sl-izgara { display: block !important; }
  #sl-baski-kok .sl-izgara > * {
    display: inline-block !important;
    width: 49% !important;
    vertical-align: top;
    box-sizing: border-box;
    margin: 0 0 12px !important;
  }
  #sl-baski-kok .sl-izgara > *:nth-child(odd) { margin-right: 1% !important; }

  /* Bölüm ve kart bütünlüğü: başlık bir sayfada, içeriği ötekinde kalmasın. */
  #sl-baski-kok article,
  #sl-baski-kok .sl-blok,
  #sl-baski-kok .sl-izgara > *,
  #sl-baski-kok li,
  #sl-baski-kok svg {
    break-inside: avoid !important;
    page-break-inside: avoid !important;
  }

  #sl-baski-kok h3 {
    break-after: avoid !important;
    page-break-after: avoid !important;
  }
}
`;

const st = {
  katman: {
    position: "fixed",
    inset: 0,
    zIndex: 9999,
    overflowY: "auto",
    background: "#eef2f6",
    padding: "0 0 60px",
  },
  arac: {
    position: "sticky",
    top: 0,
    zIndex: 2,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 16,
    flexWrap: "wrap",
    padding: "12px 20px",
    background: "#0e2a3f",
    color: "#fff",
  },
  aracSol: { display: "flex", flexDirection: "column" },
  aracAlt: { fontSize: 12, color: "#a9c2cd" },
  aracSag: { display: "flex", gap: 10, flexWrap: "wrap" },
  dugmeBirincil: {
    border: 0,
    borderRadius: 10,
    background: "#f4c542",
    color: "#111827",
    padding: "10px 18px",
    fontWeight: 800,
    fontSize: 14,
    cursor: "pointer",
  },
  dugmeIkincil: {
    border: "1px solid rgba(255,255,255,.35)",
    borderRadius: 10,
    background: "transparent",
    color: "#fff",
    padding: "10px 16px",
    fontWeight: 700,
    fontSize: 14,
    cursor: "pointer",
  },
  ipucu: {
    maxWidth: 820,
    margin: "14px auto 0",
    padding: "0 20px",
    fontSize: 12.5,
    color: "#475569",
    lineHeight: 1.6,
  },
  sayfa: {
    width: "min(820px, calc(100vw - 32px))",
    margin: "18px auto 0",
    background: "#fff",
    border: "1px solid #e2e8f0",
    borderRadius: 14,
    padding: 28,
    boxShadow: "0 18px 40px rgba(15,23,42,.10)",
  },
  baslikBandi: {
    display: "flex",
    alignItems: "center",
    gap: 20,
    paddingBottom: 16,
  },
  markaSol: { flex: "none" },
  logo: { height: 54, display: "block" },
  markaSag: { flex: 1 },
  kicker: {
    margin: 0,
    fontSize: 10.5,
    fontWeight: 800,
    letterSpacing: ".14em",
    color: "#64748b",
  },
  anaBaslik: { margin: "4px 0 0", fontSize: 24, color: "#0e2a3f", letterSpacing: "-.01em" },
  vurguCizgi: { height: 4, borderRadius: 999, margin: "0 0 18px" },
  kunye: { display: "grid", gap: 12, marginBottom: 18 },
  kunyeIsim: { display: "flex", flexDirection: "column", gap: 2 },
  kunyeEtiket: { fontSize: 10.5, fontWeight: 800, letterSpacing: ".1em", color: "#64748b", textTransform: "uppercase" },
  kunyeDeger: { fontSize: 20, color: "#0f172a" },
  kunyeKucukDeger: { fontSize: 13.5, color: "#0f172a" },
  baskinRozet: {
    display: "inline-block",
    alignSelf: "start",
    color: "#fff",
    fontSize: 12.5,
    fontWeight: 700,
    padding: "6px 12px",
    borderRadius: 999,
  },
  kunyeIzgara: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
    gap: 12,
    paddingTop: 12,
    borderTop: "1px solid #e2e8f0",
  },
  kunyeHucre: { display: "flex", flexDirection: "column", gap: 3 },
  altBilgi: {
    display: "flex",
    justifyContent: "space-between",
    gap: 16,
    flexWrap: "wrap",
    marginTop: 22,
    paddingTop: 14,
    borderTop: "1px solid #e2e8f0",
    fontSize: 11.5,
    color: "#64748b",
  },
  altBilgiSag: { textAlign: "right" },
};
